import { AppButton, AppInput } from 'components/form';
import { AppCard, AppLightTooltip, AppSpacer, AppUseIcon } from 'components/ui';
import { useCallback, useEffect, useState } from 'react';

import { EmailShouldBeValid } from 'utilities/Validations';
import { Loader } from '@trackman/web-shared-components';
import { UserData } from 'types/models/UserData';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

export const SelectUserStep = observer(
  ({ selectedUser, onSelectUser }: { selectedUser: UserData | null; onSelectUser: (user: UserData) => void }) => {
    const { usersStore, apiServiceAxiosInstance: apiInstance } = useStores();
    const [email, setEmail] = useState('');
    const [searchResult, setSearchResult] = useState<UserData[] | null>([]);
    const [isSearchCompleted, setIsSearchCompleted] = useState(false);

    useEffect(() => {
      if(email.isNullOrEmpty() || EmailShouldBeValid(email))
      {
        setSearchResult([]);
      }
    }, [email])

    useEffect(() => {
      if (!selectedUser) {
        setEmail('');
        setSearchResult([]);
        return;
      }
      if (selectedUser.email === email) return;
      setEmail(selectedUser.email);
      setSearchResult([selectedUser]);
    }, [selectedUser]);

    const onSearchHandler = useCallback(async () => {
      if (usersStore.isSearchingForNewUser) return;
      setIsSearchCompleted(false);

      if(email.isNullOrEmpty())
      {
        setSearchResult([]);
        return;
      }

      const res = await usersStore.findNewUserByEmail(email);
      if (res) {
        setSearchResult([res]);
      } else {
        setSearchResult([]);
      }
      setIsSearchCompleted(true);
    }, [email, usersStore.isSearchingForNewUser]);

    const isUserFound = useCallback(() => searchResult?.filter(Boolean) && searchResult.length > 0, [searchResult]);

    return (
      <>
        <SearchForUserComponent
          usersStore={usersStore}
          email={email}
          setEmail={setEmail}
          onSearch={onSearchHandler}
          setIsSearchCompleted={setIsSearchCompleted}
        />

        <AppSpacer space='30px' />
        {isUserFound() ? (
          <UserFoundComponent onSelectUser={onSelectUser} searchResult={searchResult} />
        ) : !usersStore.isSearchingForNewUser && isSearchCompleted && email && searchResult && searchResult.length === 0 ? (
          <AddNewUserComponent user={{ email }} onSelectUser={onSelectUser} />
        ) : null}
      </>
    );
  }
);

const AddNewUserComponent = ({ user, onSelectUser }: any) => {
  return (
    <section>
      <SingleUserComponent user={user} onSelectUser={onSelectUser} />
    </section>
  );
};

const SingleUserComponent = ({ user, onSelectUser }: any) => {
  const doesUserAlreadyExist = user && user?.id;
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);

  const isNullOrWhiteSpace = (value: string | null | undefined) => {
    return value === null || value === undefined || value.trim() === '';
  };

  const { t } = useTranslation();

  return (
    <AppCard className='p-3'>
      <div className='row'>
        <div className='col-2'>
          <img src={user.profilePhotoUrl} height={'100px'} className='rounded-circle w-100 bg-secondary aspect-ratio-1-1' alt='' />
        </div>
        <div className='col-10 d-flex flex-column position-relative'>
          <div>
            {doesUserAlreadyExist && (
              <h3 className='font-18 mb-0'>
                {user.firstName} {user.lastName}
              </h3>
            )}
            {user.email && (
              <p className='mb-0'>
                <a href={`mailto:${user.email}`} className=' mb-0 color-textElementGrey text-underline'>
                  {user.email}
                  <AppUseIcon name='ExternalLinkIcon' height='11' width='11' className='ml-2' />
                </a>
              </p>
            )}
            {user.phoneNumber && (
              <p className='mb-0'>
                <a href={`tel:${user.phoneNumber}`} className=' mb-0 color-textElementGrey text-underline'>
                  {user.phoneNumber}
                  <AppUseIcon name='ExternalLinkIcon' height='11' width='11' className='ml-2' />
                </a>
              </p>
            )}
            <p className='mb-0'>
              {user.organizationName} {user.organizationShortName ? `(${user.organizationShortName})` : ''}
            </p>

            {user.emailConfirmed ? (
              <p className='mb-0 d-flex align-items-center'>
                <AppUseIcon className='mr-2' width='14' height='14' name='SuccessCircleIcon' />
                {t('emailConfirmed')}
              </p>
            ) : (
              <p className='mb-0 d-flex align-items-center'>
                <AppUseIcon className='mr-2' width='14' height='14' name='WarningCircleIcon' />
                {t('emailNotConfirmed')}
              </p>
            )}

            {!doesUserAlreadyExist && (
              <div className='d-flex flex-row my-3'>
                <AppInput label={t('firstName')} name={'firstName'} value={firstName} onChange={(e: any) => setFirstName(e.target.value)} />
                <AppSpacer isHorizontal space='1rem' />
                <AppInput label={t('lastName')} name={'lastName'} value={lastName} onChange={(e: any) => setLastName(e.target.value)} />
              </div>
            )}
            <AppSpacer isHorizontal space='40px' />
          </div>
          <div className='position-absolute bottom-0 right-1rem d-flex'>
            <AppButton
              variant='primary'
              onClick={() =>
                onSelectUser({
                  ...user,
                  firstName: firstName || user.firstName,
                  lastName: lastName || user.lastName,
                })
              }
              className='d-flex align-items-center '
              disabled={(!doesUserAlreadyExist && (isNullOrWhiteSpace(firstName)) || (isNullOrWhiteSpace(lastName))) || EmailShouldBeValid(user.email) !== false}
            >
              <AppUseIcon name='SearchIcon' width='16' height='16' className='mr-2' color={'currentColor'} />
              {t('selectThisUser')}
            </AppButton>
            {doesUserAlreadyExist && (
              <AppLightTooltip className='tooltip_icon' title={`${t('error.USER_ALREADY_EXIST')}`} placement='top'>
                <AppUseIcon className='ml-2' width='16' height='16' name='RedInfoIcon' />
              </AppLightTooltip>
            )}
          </div>
        </div>
      </div>
    </AppCard>
  );
};

const SearchForUserComponent = ({
  usersStore,
  email,
  setEmail,
  onSearch,
  setIsSearchCompleted,
}: {
  usersStore: any;
  email: string;
  setEmail: any;
  onSearch: any;
  setIsSearchCompleted: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <section className='d-flex align-items-end justify-content-center'>
      <AppInput
        name='email'
        label={t('email')}
        value={email}
        disabled={usersStore.isSearchingForNewUser}
        validationRules={[
          EmailShouldBeValid,
          (value: string) => usersStore.users.some((user) => user.email === value) && 'USER_ALREADY_EXIST',
        ]}
        onChange={(e) => {
          setIsSearchCompleted(false);
          setEmail(e.target.value);
        }}
        onKeyDown={(e) => e.key === 'Enter' && onSearch()}
        data-test='user-dialog-email-input'
      />

      <AppButton
        variant={usersStore.isSearchingForNewUser ? 'white' : 'primary'}
        onClick={onSearch}
        disabled={usersStore.isSearchingForNewUser || !email || EmailShouldBeValid(email)}
        className='ml-2'
        width='150px'
        data-test='user-dialog-search-button'
      >
        {usersStore.isSearchingForNewUser}
        {usersStore.isSearchingForNewUser ? (
          <Loader size='small' />
        ) : (
          <>
            <AppUseIcon name='SearchIcon' color='currentColor' width='19' height='19' className=' mr-1' />
            {t('search')}
          </>
        )}
      </AppButton>
    </section>
  );
};

const UserFoundComponent = ({ onSelectUser, searchResult }: any) => {
  return (
    <section>
      {searchResult.map((user) => {
        return (
          <div className='find-user-form' key={user.id}>
            <SingleUserComponent user={user} onSelectUser={onSelectUser} />
          </div>
        );
      })}
    </section>
  );
};

export default SelectUserStep;
